import React, { useEffect, useMemo, useState } from "react";
import { FileText, Megaphone, Sparkles, BookOpen, ArrowRight, Filter } from "lucide-react";
import { useLang } from "../context/LangContext";
import api from "../lib/api";
import { FadeUp, Stagger, StaggerItem } from "../components/Motion";

const ICONS = { "white-paper": FileText, press: Megaphone, case: Sparkles, consensus: BookOpen };
const LABELS_ES = { "white-paper": "White Paper", press: "Comunicado", case: "Caso", consensus: "Consenso" };
const LABELS_EN = { "white-paper": "White Paper", press: "Press", case: "Case", consensus: "Consensus" };
const TYPES = ["white-paper", "consensus", "press", "case"];

export default function Insights() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("all");

  useEffect(() => {
    api.get("/content/insights").then(({ data }) => setData(data)).catch(() => setData({ items: [], section: {} }));
  }, []);

  const items = useMemo(() => data?.items || [], [data]);

  // Extract years from item.date (loose match — picks any 4-digit number)
  const years = useMemo(() => {
    const ys = new Set();
    items.forEach((it) => {
      const m = String(it.date || "").match(/\b(20\d{2})\b/);
      if (m) ys.add(m[1]);
    });
    return Array.from(ys).sort().reverse();
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (typeFilter !== "all" && it.type !== typeFilter) return false;
      if (yearFilter !== "all") {
        const m = String(it.date || "").match(/\b(20\d{2})\b/);
        if (!m || m[1] !== yearFilter) return false;
      }
      return true;
    });
  }, [items, typeFilter, yearFilter]);

  const sectionTitle = data?.section
    ? (lang === "en" ? data.section.title_en : data.section.title_es) || (lang === "en" ? "Convergen Thinking" : "Pensamiento Convergen")
    : (lang === "en" ? "Convergen Thinking" : "Pensamiento Convergen");
  const sectionEyebrow = (lang === "en" ? data?.section?.eyebrow_en : data?.section?.eyebrow_es) || (lang === "en" ? "IIEAS · Archive" : "IIEAS · Archivo");
  const intro = (lang === "en" ? data?.intro_en : data?.intro_es) || "";

  return (
    <div className="paper-bg" data-testid="page-insights">
      {/* HEADER */}
      <section className="section-pad">
        <div className="page-container">
          <FadeUp>
            <p className="eyebrow mb-6">{sectionEyebrow}</p>
            <h1 className="iieas-display text-5xl md:text-6xl lg:text-7xl max-w-4xl leading-[1.05]">{sectionTitle}</h1>
            <div className="mt-10 h-px w-24 bg-ink/60" />
            {intro && (
              <p className="mt-10 text-base md:text-lg text-text-muted leading-relaxed max-w-3xl">
                {intro}
              </p>
            )}
          </FadeUp>
        </div>
      </section>

      <div className="page-container"><div className="h-px bg-ink/15" /></div>

      {/* FILTERS */}
      <section className="py-10 md:py-12">
        <div className="page-container">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-[11px] font-mono tracking-[0.22em] uppercase text-cerulean">
              <Filter className="w-4 h-4" strokeWidth={1.5} />
              {lang === "en" ? "Filter" : "Filtrar"}
            </div>

            {/* Type filter */}
            <div className="flex flex-wrap gap-2" data-testid="filter-type">
              <Chip active={typeFilter === "all"} onClick={() => setTypeFilter("all")} testid="type-all">
                {lang === "en" ? "All" : "Todos"}
              </Chip>
              {TYPES.map((tp) => (
                <Chip key={tp} active={typeFilter === tp} onClick={() => setTypeFilter(tp)} testid={`type-${tp}`}>
                  {(lang === "en" ? LABELS_EN : LABELS_ES)[tp]}
                </Chip>
              ))}
            </div>

            {years.length > 0 && (
              <div className="flex flex-wrap gap-2 md:ml-4 md:pl-4 md:border-l border-ink/15" data-testid="filter-year">
                {["all", ...years].map((y) => (
                  <Chip key={y} active={yearFilter === y} onClick={() => setYearFilter(y)} testid={`year-${y}`}>
                    {y === "all" ? (lang === "en" ? "All years" : "Todos los años") : y}
                  </Chip>
                ))}
              </div>
            )}

            <div className="ml-auto text-[11px] font-mono tracking-widest text-text-muted">
              {filtered.length} / {items.length}
            </div>
          </div>
        </div>
      </section>

      {/* LIST */}
      <section className="pb-24 md:pb-32">
        <div className="page-container">
          {filtered.length === 0 ? (
            <div className="py-24 text-center text-sm text-text-muted" data-testid="insights-empty">
              {lang === "en" ? "No publications match the current filter." : "Sin publicaciones para el filtro actual."}
            </div>
          ) : (
            <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/15">
              {filtered.map((it, i) => {
                const Icon = ICONS[it.type] || FileText;
                const typeLabel = (lang === "en" ? LABELS_EN : LABELS_ES)[it.type] || "";
                const title = lang === "en" ? it.title_en : it.title_es;
                const summary = lang === "en" ? it.summary_en : it.summary_es;
                const Wrapper = it.link ? "a" : "div";
                const wrapperProps = it.link ? { href: it.link, target: "_blank", rel: "noopener noreferrer" } : {};
                return (
                  <StaggerItem key={i}>
                    <Wrapper
                      {...wrapperProps}
                      className="block bg-white p-8 h-full group hover:bg-pearl transition-colors"
                      data-testid={`archive-item-${i + 1}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-cerulean" strokeWidth={1.5} />
                          <span className="text-[10px] font-mono tracking-[0.22em] uppercase text-cerulean">{typeLabel}</span>
                        </div>
                        {it.date && <span className="text-[10px] font-mono tracking-widest text-text-muted">{it.date}</span>}
                      </div>
                      <h3 className="mt-8 iieas-display text-2xl leading-snug">{title}</h3>
                      <p className="mt-4 text-sm md:text-base text-text-muted leading-relaxed">
                        {summary}
                      </p>
                      {it.link && (
                        <div className="mt-6 inline-flex items-center gap-2 text-[11px] font-mono tracking-[0.2em] uppercase text-ink group-hover:text-cerulean transition-colors">
                          {lang === "en" ? "Read" : "Leer"} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </div>
                      )}
                    </Wrapper>
                  </StaggerItem>
                );
              })}
            </Stagger>
          )}
        </div>
      </section>
    </div>
  );
}

function Chip({ active, onClick, testid, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 text-[11px] font-mono tracking-[0.16em] uppercase border transition-all ${
        active ? "border-cerulean bg-cerulean text-white" : "border-rule text-ink hover:border-cerulean"
      }`}
    >
      {children}
    </button>
  );
}
